
import { TranslationRecord } from '../../types';

export const rentalTerms: TranslationRecord = {
  // Rental Conditions Page - English translations
  rentalTermsTitle: 'Rental Terms and Conditions',
  effectiveDate: 'Effective Date: May 4, 2025',
  
  // Introduction
  introductionTitle: '1. Introduction',
  overviewLabel: 'Overview:',
  overview: 'These terms and conditions govern your stay at Loena Luxury Suites. By making a reservation, you agree to abide by these terms.',
  availabilityLabel: 'Availability:',
  availability: 'These terms are always available on our website for reference.',
  
  // Reservation
  reservationTitle: '2. Booking and Guest Information',
  guestRegistrationLabel: 'Guest Registration:',
  guestRegistration: 'All guests must provide accurate and complete information when booking. Failure to provide such information may result in the cancellation of the booking.',
  identificationLabel: 'Identification:',
  identification: 'Valid government-issued photo identification is required for all guests at check-in. In accordance with Greek law, if you fail to present a valid ID at check-in, Loena Luxury Suites reserves the right to deny access to the property.',
  maxOccupancyLabel: 'Maximum Occupancy:',
  maxOccupancy: 'Suites accommodate up to 3 guests. An additional charge of €20 per night applies for the third guest.',
  unregisteredGuestsLabel: 'Unregistered Guests:',
  unregisteredGuests: 'Only guests registered at the time of booking are permitted to be in the suite. Any additional individuals who are not registered as part of the booking will not be allowed to be present in the suite.',
  contagiousDiseasesLabel: 'Contagious Diseases:',
  contagiousDiseases: 'Guests suffering from a contagious disease or exhibiting symptoms of such diseases, including but not limited to COVID-19, are not permitted to stay at the property. This is to ensure the safety and health of all guests. If a guest is found to have a contagious disease, the rental agreement will be terminated immediately, and the guest will be required to vacate the premises without a refund. Additionally, the guest will be responsible for any professional disinfection costs incurred by Loena Luxury Suites for sanitizing the accommodation.',

  // Payment and Cancellation
  paymentCancellationTitle: '3. Payment and Cancellation Policies',
  acceptedPaymentMethodsLabel: 'Accepted Payment Methods:',
  acceptedPaymentMethods: 'Visa, MasterCard, American Express, bank transfers, and cash (up to €500).',
  paymentTimingLabel: 'Payment Timing:',
  paymentTiming: 'Full payment is due at the end of the cancellation period for refundable bookings, or immediately upon booking for non-refundable rates. Failure to provide payment in full upon check-in will result in immediate termination of the rental agreement.',
  cancellationTermsLabel: 'Cancellation Terms:',
  cancellationTerms: 'Cancellations are subject to the rate and booking channel terms applicable to the specific reservation.',
  noShowLabel: 'No-show:',
  noShow: 'In case of a no-show, the guest has no right to a refund and the full amount will be charged.',
  specialOffersLabel: 'Special Offers:',
  specialOffers: 'Promotional rates may have different cancellation terms, which will be clearly communicated at the time of booking.',
  
  // Check-in and Check-out
  checkInOutTitle: '4. Check-in and Check-out Procedures',
  checkInTimeLabel: 'Check-in Time:',
  checkInTime: 'From 3:00 PM.',
  checkOutTimeLabel: 'Check-out Time:',
  checkOutTime: 'By 11:00 AM.',
  frontDeskHoursLabel: 'Front Desk Hours:',
  frontDeskHours: 'Weekdays: 8:30 AM – 3:30 PM, Weekends and Public Holidays: 11:00 AM – 3:30 PM. After-hours check-in requires prior online check-in, to be completed by 9:30 PM.',
  earlyCheckInLateCheckOutLabel: 'Early Check-in / Late Check-out:',
  earlyCheckInLateCheckOut: 'Subject to availability and may incur additional charges.',
  failureToPresentIDLabel: 'Failure to Present ID or Payment:',
  failureToPresentID: 'In accordance with Greek law, if the guest does not present a valid ID or has not fully paid for the reservation at check-in, Loena Luxury Suites has the right to deny check-in and terminate the rental agreement.',
  
  // House Rules
  houseRulesTitle: '5. House Rules and Guest Conduct',
  prohibitedActivitiesTitle: 'Prohibited Activities:',
  prohibitedActivities1: 'Smoking (including vaping)',
  prohibitedActivities2: 'Pets',
  prohibitedActivities3: 'Parties or gatherings',
  prohibitedActivities4: 'Professional activities',
  prohibitedActivities5: 'Lighting candles',
  prohibitedActivities6: 'Flammable devices (e.g., gasoline, portable gas stoves)',
  
  quietHoursTitle: 'Quiet Hours:',
  summerQuietHours: 'Summer (April 1 - September 30): 3:00 PM – 5:30 PM, 11:00 PM – 7:00 AM',
  winterQuietHours: 'Winter (October 1 - March 31): 3:30 PM – 5:30 PM, 10:00 PM – 7:30 AM',
  
  guestResponsibilitiesTitle: 'Guest Responsibilities:',
  guestResponsibilities1: 'Guests must maintain cleanliness and respect for the property.',
  guestResponsibilities2: 'Guests are expected to act respectfully toward neighbors and property staff. Disruptive behavior may result in the termination of the rental agreement without refund.',
  guestResponsibilities3: 'Guests must comply with all health and safety protocols and adhere to energy conservation practices.',
  
  // Violation of Agreement
  violationTitle: '6. Violation of Agreement and Termination',
  violationIntro: 'The following actions or omissions constitute violations of this rental agreement and may result in immediate termination of the rental agreement, requiring the guest to vacate the property without refund:',
  failureToPayLabel: 'Failure to Present ID or Pay:',
  failureToPay: 'Failure to present a valid government-issued photo ID or non-payment at check-in as required by Greek law.',
  unregisteredGuestsViolationLabel: 'Unregistered Guests:',
  unregisteredGuestsViolation: 'Allowing guests who are not registered at the time of booking to stay overnight.',
  contagiousDiseasesViolationLabel: 'Contagious Diseases:',
  contagiousDiseasesViolation: 'Any guest found to be suffering from a contagious disease or exhibiting symptoms of such a disease, including but not limited to COVID-19.',
  prohibitedActivitiesViolationLabel: 'Prohibited Activities:',
  prohibitedActivitiesViolation: 'Engaging in smoking (including vaping), bringing pets, holding parties or gatherings, performing professional activities, or lighting candles within the property.',
  disruptiveBehaviorLabel: 'Disruptive Behavior:',
  disruptiveBehavior: 'Behavior that disrupts the peace, safety, or comfort of other guests, neighbors, or property staff.',
  violationOfHealthLabel: 'Violation of Health and Safety Protocols:',
  violationOfHealth: 'Failure to comply with health and safety measures, including energy conservation guidelines or other safety-related instructions.',
  failureToReportDamagesLabel: 'Failure to Report Damages:',
  failureToReportDamages: 'Failure to report any damages or incidents promptly, as outlined in the Damage Reporting section.',
  violationOfQuietHoursLabel: 'Violation of Quiet Hours:',
  violationOfQuietHours: 'Disregarding the quiet hours established in the House Rules section.',

  // Property Access and Restrictions
  propertyAccessTitle: '7. Property Access and Restrictions',
  accessLabel: 'Access:',
  access: '24/7 access with access codes provided.',
  internetUseLabel: 'Internet Use:',
  internetUse: 'Complimentary Wi-Fi (up to 100 Mbps) is available. Loena Luxury Suites is not liable for any issues that arise due to the use of the internet during your stay, including, but not limited to, data loss, viruses, or security breaches. Guests are responsible for their online activities and agree not to engage in any illegal activities or access illegal content during their stay. Violations will be reported to the authorities.',
  guestSecurityLabel: 'Guest Security:',
  guestSecurity: 'Guests are responsible for securing their personal belongings. A safe is available in each suite, and Loena Luxury Suites is not liable for any loss or damage to items stored in the safe.',
  
  // Security and Liability
  securityLiabilityTitle: '8. Security and Liability',
  liabilityLabel: 'Liability:',
  liability: 'Loena Luxury Suites is not liable for the loss, damage, or theft of personal belongings during the guest\'s stay.',
  insuranceLabel: 'Insurance:',
  insurance: 'Guests are strongly advised to obtain travel insurance to cover potential risks such as cancellations, medical emergencies, or personal property loss.',
  
  // Force Majeure
  forceMajeureTitle: '9. Force Majeure',
  liabilityForUnforeseenEventsLabel: 'Liability for Unforeseen Events:',
  liabilityForUnforeseenEvents: 'Loena Luxury Suites is not liable for any failure to perform its obligations due to events beyond its control, such as natural disasters, government actions, or health-related emergencies.',
  
  // Privacy and Data Protection
  privacyDataProtectionTitle: '10. Privacy and Data Protection',
  personalDataLabel: 'Personal Data:',
  personalData: 'All personal data will be collected and processed in accordance with our privacy policy. This data may be shared with local authorities as required by Greek law.',
  
  // Jurisdiction and Applicable Law
  jurisdictionTitle: '11. Jurisdiction and Applicable Law',
  governingLawLabel: 'Governing Law:',
  governingLaw: 'These terms and conditions, and any disputes arising out of or related to the guest\'s stay at Loena Luxury Suites, shall be governed by and construed in accordance with the laws of Greece.',
  jurisdictionLabel: 'Jurisdiction:',
  jurisdiction: 'Any legal action, dispute, or proceeding arising out of or relating to these terms and conditions or the guest\'s stay shall fall under the exclusive jurisdiction of the Greek courts in Thessaloniki. Both parties consent to the personal jurisdiction of such courts and waive any objection to the laying of venue in such courts.',
  languageOfProceedingsLabel: 'Language of Proceedings:',
  languageOfProceedings: 'Any legal proceedings shall be conducted in the Greek language, with official translations provided as necessary.',
  disputeResolutionLabel: 'Dispute Resolution:',
  disputeResolution: 'Before initiating any legal action, guests are encouraged to first contact Loena Luxury Suites to attempt to resolve any disputes amicably.',

  // Communication and Emergencies
  contactInfoTitle: '12. Communication and Emergencies',
  emailLabel: 'Email:',
  phoneLabel: 'Phone:',
  addressLabel: 'Address:',
  address: 'Georgiou Stavrou 5, Thessaloniki 546 40, Greece',

  // Lost and Found
  lostFoundTitle: '13. Lost and Found',
  lostFoundLabel: 'Lost and Found:',
  lostFoundProcess: 'Any items left behind by guests will be held for a maximum of 30 days. Guests may arrange for the return of their belongings by contacting us at <a href="mailto:christos@loenasuites.com" class="text-loena-gold hover:underline">christos@loenasuites.com</a>. Shipping costs will be charged to the guest.',
  
  // Damage Reporting
  damageReportingTitle: '14. Damage Reporting',
  reportingDamagesLabel: 'Reporting Damages:',
  reportingDamages: 'Guests must report any damages or incidents immediately to ensure prompt resolution and avoid potential charges. Failure to report damages may result in the guest being held responsible for repair costs.'
};
